import { z } from "zod";
const stringParseableAsNumber = z.string().refine((value)=>{
    return !isNaN(Number(value));
}, {
    message: "String is not parseable as a number."
});
export const ProductSchema = z.object({
    rawPrice: z.string(),
    // Localized price
    price: z.string(),
    period: z.string(),
    periodAlt: z.string(),
    localizedPeriod: z.string(),
    // Period (unlocalized)
    // Periodly (unlocalized)
    periodly: z.string(),
    weeklyPrice: z.string(),
    dailyPrice: z.string(),
    monthlyPrice: z.string(),
    yearlyPrice: z.string(),
    rawTrialPeriodPrice: stringParseableAsNumber,
    trialPeriodPrice: z.string(),
    trialPeriodDailyPrice: z.string(),
    trialPeriodWeeklyPrice: z.string(),
    trialPeriodMonthlyPrice: z.string(),
    trialPeriodYearlyPrice: z.string(),
    trialPeriodDays: z.string(),
    trialPeriodWeeks: z.string(),
    trialPeriodMonths: z.string(),
    trialPeriodYears: z.string(),
    trialPeriodText: z.string(),
    trialPeriodEndDate: z.string(),
    periodDays: z.string(),
    periodWeeks: z.string(),
    periodMonths: z.string(),
    periodYears: z.string(),
    locale: z.string(),
    languageCode: z.string(),
    currencyCode: z.string(),
    currencySymbol: z.string(),
    identifier: z.string()
});
export const ProductPropertiesDefinition = {
    rawPrice: {
        description: "The raw price of the product without currency symbol",
        exampleValues: [
            "9.99",
            "0.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    price: {
        description: "The price of the product",
        exampleValues: [
            "$9.99",
            "$0.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    period: {
        description: "The recurring period of the product",
        exampleValues: [
            "month",
            "year"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodAlt: {
        description: "An alternative localized period",
        exampleValues: [
            "1 yr"
        ],
        availableSinceVersion: "3.0.0"
    },
    localizedPeriod: {
        description: "The localized period of the product",
        exampleValues: [
            "1 yr",
            "1 m"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodly: {
        description: "The recurring period of the product",
        exampleValues: [
            "monthly",
            "yearly"
        ],
        availableSinceVersion: "3.0.0"
    },
    weeklyPrice: {
        description: "The weekly price of the product",
        exampleValues: [
            "$1.73"
        ],
        availableSinceVersion: "3.0.0"
    },
    dailyPrice: {
        description: "The daily price of the product",
        exampleValues: [
            "$0.25"
        ],
        availableSinceVersion: "3.0.0"
    },
    monthlyPrice: {
        description: "The monthly price of the product",
        exampleValues: [
            "$9.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    yearlyPrice: {
        description: "The yearly price of the product",
        exampleValues: [
            "$99.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    rawTrialPeriodPrice: {
        description: "The raw trial period price of the product without currency symbol, if any",
        exampleValues: [
            "9.99",
            "0.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodPrice: {
        description: "The trial period price of the product, if any",
        exampleValues: [
            "$9.99",
            "$0.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodDailyPrice: {
        description: "The trial period daily price of the product, if any",
        exampleValues: [
            "$0.25"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodWeeklyPrice: {
        description: "The trial period weekly price of the product, if any",
        exampleValues: [
            "$1.73"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodMonthlyPrice: {
        description: "The trial period monthly price of the product, if any",
        exampleValues: [
            "$9.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodYearlyPrice: {
        description: "The trial period yearly price of the product, if any",
        exampleValues: [
            "$99.99"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodDays: {
        description: "The trial period days of the product, if any",
        exampleValues: [
            "7"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodWeeks: {
        description: "The trial period weeks of the product, if any",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodMonths: {
        description: "The trial period months of the product, if any",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodYears: {
        description: "The trial period years of the product, if any",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodText: {
        description: "The trial period text of the product, if any",
        exampleValues: [
            "7 days"
        ],
        availableSinceVersion: "3.0.0"
    },
    trialPeriodEndDate: {
        description: "The trial period end date of the product, if any",
        exampleValues: [
            "Sep 28, 2023"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodDays: {
        description: "The period days of the product",
        exampleValues: [
            "7"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodWeeks: {
        description: "The period weeks of the product",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodMonths: {
        description: "The period months of the product",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    periodYears: {
        description: "The period years of the product",
        exampleValues: [
            "1"
        ],
        availableSinceVersion: "3.0.0"
    },
    locale: {
        description: "The locale of the product",
        exampleValues: [
            "en_US"
        ],
        availableSinceVersion: "3.0.0"
    },
    languageCode: {
        description: "The language code of the product",
        exampleValues: [
            "en"
        ],
        availableSinceVersion: "3.0.0"
    },
    currencyCode: {
        description: "The currency code of the product",
        exampleValues: [
            "USD"
        ],
        availableSinceVersion: "3.0.0"
    },
    currencySymbol: {
        description: "The currency symbol of the product",
        exampleValues: [
            "$"
        ],
        availableSinceVersion: "3.0.0"
    },
    identifier: {
        description: "The identifier of the product",
        exampleValues: [
            "com.superwall.subscription.1m"
        ],
        availableSinceVersion: "3.0.0"
    }
};
